<template>
  <Loader :value="promises.fetchOne" :message="$t('loading', { resource: label.toLowerCase() }) + '...'" class="posts">
    <div class="container view">
      <HeaderComponent
        :title="$metaInfo.title"
        :breadcrumb="breadcrumb"
        :empty-state-portal-name="!Object.keys(filter).length ? actionPortalName : null"
      >
        <template #actions>
          <Actions :placement="actionBase" :query="{ reference: id }" />

          <Actions
            v-if="collection"
            :placement="actionBase + ':add'"
            :label="$t('add')"
            :icon="['fal', 'plus']"
            theme="primary"
            :query="{ reference: id, collection }"
          />

          <ButtonComponent
            v-else
            tag="router-link"
            :to="{
              name: 'post-create',
              query: { reference, returnTo: $route.path, config },
            }"
            :label="$t('add')"
            :icon="['fal', 'plus']"
            theme="primary"
          />
        </template>
        <template #right>
          <FilterComponent v-if="filterOptions" v-model="filter" v-bind="filterOptions" />
        </template>
      </HeaderComponent>
      <div class="posts__content">
        <Card
          v-for="item in items"
          :key="item.id"
          :value="item"
          :icon="getPostIcon(item)"
          :tags="getPostTags(item)"
          :to="{
            name: 'post-edit',
            params: { id: item.id },
            query: { reference, collection, returnTo: $route.fullPath },
          }"
        />
      </div>
      <EmptyState
        v-if="!items.length"
        :icon="$path('icon', postCollection || { icon: ['fal', 'newspaper'] })"
        :title="$t('noX', { x: label.toLowerCase() })"
        action-portal
        :action-portal-name="actionPortalName"
      />
    </div>
  </Loader>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { Debounce, ObjectToDotNotation } from '@/utils'

import HeaderComponent from '@/components/Header'
import Card from '@/components/Post/Card'
import Actions from '@/components/Actions'
import EmptyState from '@/components/EmptyState'
import FilterComponent from '@/components/Filter/Component'

let getLabel = (label = '', count = 1) => label.split('|')[count > 1 ? 'pop' : 'shift']()

export default {
  data() {
    return {
      filter: {},
    }
  },

  watch: {
    customFieldsFilter: {
      handler: 'fetch',
      immediate: true,
    },
  },

  props: {
    id: String,
    collection: String,
    actionPortalName: {
      type: String,
      default: 'empty-state-actions',
    },
    embedded: Boolean,
  },

  computed: {
    ...mapState('Post', ['item', 'promises']),
    postCollection() {
      switch (this.$route.name) {
        case 'project-edit':
        case 'page-edit':
          return (this.$path('metadata.postCollections', this.item) || []).find(p => this.collection === p.id)
      }
    },

    customFieldsFilter() {
      return Object.entries(this.filter || {})
        .map(([k, v]) => {
          if (!k.includes('customFields.')) return {}
          return { key: k.replace('customFields.', ''), value: v }
        })
        .filter(i => Boolean(i.value))
    },

    filterOptions() {
      const filters = this.$path('config.filters', this.postCollection) || []

      return {
        items: filters.map(({ id }) => id),

        options: Object.fromEntries(
          filters.map(({ id, label, options = {} }) => [
            id.split(':').pop(),
            { ...options, label: label || options.label || id },
          ]),
        ),
      }
    },

    label() {
      return this.getLabel(2)
    },
    items() {
      return this.$path('item.posts') || []
    },

    actionBase() {
      switch (this.$route.name) {
        default:
          return this.$route.name
        case 'page-edit':
          return `page-posts`
        case 'project-edit':
          return `project-posts`
      }
    },
    reference() {
      switch (this.$route.name) {
        case 'project-edit':
          return `Project:${this.id}`
        case 'page-edit':
          return `Page:${this.id}`
      }
    },
    config() {
      switch (this.$route.name) {
        case 'project-edit':
        case 'page-edit':
          return (this.$path('item.metadata.defaultPostConfig') || []).map(e => e.source)
      }
    },
    breadcrumb() {
      return [{ title: this.label }]
    },
    query() {
      switch (this.$route.name) {
        case 'project-edit':
          return 'ProjectPosts'
        case 'page-edit':
          return 'PagePosts'
      }
    },
  },
  methods: {
    ...mapActions('Post', {
      fetch(dispatch) {
        return dispatch('fetch', [
          {
            id: this.id,
            postFilter: {
              collection: this.collection,
              customFields: this.customFieldsFilter,
            },
          },
          { query: this.query },
        ])
      },
    }),
    getPostIcon(post) {
      if (!this.postCollection) return ['fal', 'newspaper']
      return Array.from(
        new Set(
          post.customFieldsConfig
            .map(c => {
              let target = this.postCollection.schemas.find(s => s.source === c.source)
              return (target && target.icon) || this.postCollection.icon
            })
            .filter(Boolean),
        ),
      )
    },
    getPostTags(post) {
      if (!this.postCollection) return []
      const tags = this.$path('config.tags', this.postCollection) || []

      return Array.from(
        new Set([
          ...(this.postCollection.schemas.length <= 1
            ? []
            : post.customFieldsConfig
                .map(c => {
                  let target = this.postCollection.schemas.find(s => s.source === c.source)
                  if (!target || !target.label) return
                  return getLabel(target.label)
                })
                .filter(Boolean)),
          ...tags
            .map(t => {
              const val = this.$path(t.id, post)

              if (!val) return

              return t.i18nPrefix ? this.$t(`${t.i18nPrefix}:${val}`) : val
            })
            .filter(Boolean),
        ]),
      )
    },
    getLabel(count = 1) {
      let target =
        this.$path('postCollection.schemas.length') > 1
          ? this.$path('postCollection.label')
          : this.$path('postCollection.schemas.0.label') || this.$path('postCollection.label')
      return getLabel(target || this.$t('post'), count)
    },
  },

  metaInfo() {
    if (this.embedded) return {}
    return {
      title: this.label,
    }
  },

  components: {
    Card,
    HeaderComponent,
    Actions,
    EmptyState,
    FilterComponent,
  },
}
</script>

<style lang="scss">
.posts {
  background-color: $background-grey;

  .filter {
    flex-direction: row-reverse;
  }
  .header__right {
    align-items: flex-end;
  }

  &__content {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    grid-gap: $form-gap;
  }

  &--scoped {
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius);

    .container {
      padding: 1.5rem;
    }

    .view {
      min-height: initial;
    }

    .header {
      position: static !important;
      padding: 0;
    }

    .header__title,
    .header__breadcrumb {
      display: none;
    }
  }
}
</style>
